/**
 * Client-side API pour les thèmes contextuels.
 *
 * - listAccessibleThemes / getThemeBySlug : lecture via Supabase (RLS appliquée)
 * - createTheme / submitForReview / moderateTheme : appels aux edge functions
 */

import { supabase } from '@/lib/supabase';
import type { ExerciseDifficulty, SchoolLevel, SubjectSlug, Theme } from '@jeraisonne/db';
import type { CreateThemeInput, ModerateThemeInput } from '@jeraisonne/db/schemas/themes';

export interface ThemeExerciseListItem {
  id: string;
  title: string;
  difficulty: ExerciseDifficulty;
  is_primary: boolean;
  /** Cover de l'exercice (illustration manga générée par le pipeline). */
  illustration_url: string | null;
  /** Mise en situation partagée — sert de clé de regroupement par contexte. */
  context_md: string | null;
  chapter: {
    id: string;
    slug: string;
    title: string;
    subject: SubjectSlug;
    level: SchoolLevel;
  };
}

const SELECT_COLS =
  'id, slug, title, short_description, description_md, illustration_url, illustration_prompt, min_level, sensitivity, topical_until, status, visibility, author_id, exercise_count, created_at, updated_at, published_at';

export async function listAccessibleThemes(filters?: {
  minLevel?: string | null;
  search?: string;
}): Promise<Theme[]> {
  let q = supabase
    .from('themes')
    .select(SELECT_COLS)
    .neq('status', 'archived')
    .order('exercise_count', { ascending: false });

  if (filters?.minLevel) q = q.eq('min_level', filters.minLevel as SchoolLevel);
  if (filters?.search?.trim()) q = q.ilike('title', `%${filters.search.trim()}%`);

  const { data, error } = await q;
  if (error) throw error;
  return (data ?? []) as unknown as Theme[];
}

export async function getThemeBySlug(slug: string): Promise<Theme | null> {
  const { data, error } = await supabase
    .from('themes')
    .select(SELECT_COLS)
    .eq('slug', slug)
    .maybeSingle();
  if (error) throw error;
  return (data as unknown as Theme) ?? null;
}

export async function listAuthoredThemes(authorId: string): Promise<Theme[]> {
  const { data, error } = await supabase
    .from('themes')
    .select(SELECT_COLS)
    .eq('author_id', authorId)
    .order('updated_at', { ascending: false });
  if (error) throw error;
  return (data ?? []) as unknown as Theme[];
}

export async function listPendingReviewThemes(): Promise<Theme[]> {
  const { data, error } = await supabase
    .from('themes')
    .select(SELECT_COLS)
    .eq('visibility', 'pending_review')
    .order('updated_at', { ascending: true });
  if (error) throw error;
  return (data ?? []) as unknown as Theme[];
}

export async function createTheme(input: CreateThemeInput): Promise<Theme> {
  const res = await supabase.functions.invoke<{ theme: Theme }>('themes-create', {
    body: input,
  });
  if (res.error) throw new Error(res.error.message);
  if (!res.data?.theme) throw new Error('Réponse vide');
  return res.data.theme;
}

export async function submitThemeForReview(themeId: string): Promise<Theme> {
  const res = await supabase.functions.invoke<{ theme: Theme }>('themes-submit-for-review', {
    body: { themeId },
  });
  if (res.error) throw new Error(res.error.message);
  if (!res.data?.theme) throw new Error('Réponse vide');
  return res.data.theme;
}

export async function moderateTheme(input: ModerateThemeInput): Promise<Theme> {
  const res = await supabase.functions.invoke<{ theme: Theme }>('themes-moderate', {
    body: input,
  });
  if (res.error) throw new Error(res.error.message);
  if (!res.data?.theme) throw new Error('Réponse vide');
  return res.data.theme;
}

export async function listThemeExercises(themeId: string): Promise<ThemeExerciseListItem[]> {
  const { data, error } = await supabase
    .from('exercise_themes')
    .select(
      `is_primary,
       exercises!inner (
         id, title, difficulty, illustration_url, context_md, status,
         chapters!inner ( id, slug, title, subject, level )
       )`,
    )
    .eq('theme_id', themeId)
    .eq('exercises.status', 'published')
    .order('is_primary', { ascending: false });
  if (error) throw error;
  return (data ?? [])
    .map((row) => {
      const ex = (row as { exercises: unknown }).exercises as {
        id: string;
        title: string;
        difficulty: ExerciseDifficulty;
        illustration_url: string | null;
        context_md: string | null;
        chapters: {
          id: string;
          slug: string;
          title: string;
          subject: SubjectSlug;
          level: SchoolLevel;
        } | null;
      } | null;
      if (!ex || !ex.chapters) return null;
      return {
        id: ex.id,
        title: ex.title,
        difficulty: ex.difficulty,
        is_primary: Boolean((row as { is_primary: boolean }).is_primary),
        illustration_url: ex.illustration_url,
        context_md: ex.context_md,
        chapter: ex.chapters,
      } satisfies ThemeExerciseListItem;
    })
    .filter((e): e is ThemeExerciseListItem => e !== null);
}

export async function withdrawThemeToPrivate(themeId: string): Promise<Theme> {
  // Simple UPDATE direct : la RLS l'autorise pour l'auteur (transitions
  // private/pending_review). Pour public → private, l'auteur peut aussi via
  // policy "update_author" car visibility cible est private ∈ (private, pending_review).
  const { data, error } = await supabase
    .from('themes')
    .update({ visibility: 'private' })
    .eq('id', themeId)
    .select(SELECT_COLS)
    .single();
  if (error) throw error;
  return data as unknown as Theme;
}
